import { ChangeDetectionStrategy, Component, booleanAttribute, input, output } from '@angular/core';
import { ICHING_LIMITS } from '../../core/iching-limits.const';
import { injectIChingTranslate } from '../iching-consult-translate';

type PracticeList = 'do' | 'dont';

/**
 * Praktijk (C6): wat je gaat doen en wat je laat, de aangestreepte woorden,
 * eigen notities, bewaren in het dagboek of kopiëren als tekst, en (eens
 * bewaard) markeren als vandaag herlezen.
 */
@Component({
  selector: 'hd-iching-practice',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host { display: block; font-size: 13.5px; line-height: 1.5; }
    .lists { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .list {
      display: flex; flex-direction: column; gap: 6px; padding: 12px 14px;
      border-radius: var(--radius-md, 6px); background: var(--hd-iching-card-bg, rgba(0, 0, 0, 0.035));
    }
    .label { font-weight: 600; }
    ul { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 4px; }
    li { display: flex; align-items: flex-start; gap: 6px; }
    li span { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
    .remove {
      flex: 0 0 auto; width: 22px; height: 22px; padding: 0; border: 0; border-radius: 50%;
      background: transparent; color: inherit; cursor: pointer; opacity: 0.6;
    }
    .remove:hover { opacity: 1; background: rgba(0, 0, 0, 0.06); }
    .add { display: flex; gap: 6px; }
    .field, .notes {
      width: 100%; box-sizing: border-box; padding: 6px 10px; font: inherit; font-size: 13.5px; color: inherit;
      border: 1px solid var(--hd-iching-muted-border, rgba(0, 0, 0, 0.18)); border-radius: var(--radius-md, 6px);
      background: var(--hd-iching-input-bg, transparent);
    }
    .add-btn {
      flex: 0 0 auto; width: 32px; border: 1px solid var(--hd-iching-muted-border, rgba(0, 0, 0, 0.18));
      border-radius: var(--radius-md, 6px); background: transparent; color: inherit; cursor: pointer;
    }
    .block { margin-top: 14px; display: flex; flex-direction: column; gap: 6px; }
    .chips { display: flex; flex-wrap: wrap; gap: 6px; }
    .chip {
      display: inline-flex; align-items: center; gap: 4px; padding: 2px 10px; border-radius: 999px; font: inherit; font-size: 12.5px;
      border: 1px solid var(--hd-iching-word-marked-border, rgba(212, 168, 105, 0.7));
      background: var(--hd-iching-word-marked, rgba(212, 168, 105, 0.25)); color: inherit; cursor: pointer;
    }
    .notes { min-height: 90px; resize: vertical; line-height: 1.45; }
    .revisit { margin: 14px 0 0; font-style: italic; opacity: 0.85; }
    .actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 14px; }
    .primary {
      padding: 6px 16px; border: 1px solid var(--primary, #3c4650); border-radius: var(--radius-md, 6px);
      background: var(--primary, #3c4650); color: var(--hd-iching-on-primary, #fff); font: inherit; font-weight: 600; cursor: pointer;
    }
    .primary:disabled { opacity: 0.6; cursor: default; }
    .secondary {
      padding: 6px 14px; border: 1px solid var(--hd-iching-muted-border, rgba(0, 0, 0, 0.18)); border-radius: var(--radius-md, 6px);
      background: transparent; color: inherit; font: inherit; cursor: pointer;
    }
    .secondary:disabled { opacity: 0.6; cursor: default; }
    .saved { font-size: 12.5px; opacity: 0.8; }
    @media (max-width: 599.98px) {
      .lists { grid-template-columns: 1fr; }
      .actions > button { flex: 1 1 auto; }
    }
  `,
  template: `
    <div class="lists">
      @for (kind of kinds; track kind) {
        <div class="list" [attr.data-list]="kind">
          <span class="label">{{ t('humanDesign.iching.practice.' + kind) }}</span>
          @if (listOf(kind).length > 0) {
            <ul>
              @for (item of listOf(kind); track $index) {
                <li>
                  <span>{{ item }}</span>
                  <button type="button" class="remove" [attr.aria-label]="item" (click)="remove(kind, $index)">
                    <i class="fas fa-xmark" aria-hidden="true"></i>
                  </button>
                </li>
              }
            </ul>
          }
          <div class="add">
            <input
              #field
              type="text"
              class="field"
              [attr.data-test-id]="'iching-' + kind + '-input'"
              [placeholder]="t('humanDesign.iching.practice.addPlaceholder')"
              (keydown.enter)="add(kind, field); $event.preventDefault()"
            />
            <button type="button" class="add-btn" [attr.aria-label]="t('humanDesign.iching.practice.' + kind)" (click)="add(kind, field)">
              <i class="fas fa-plus" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      }
    </div>

    @if (keywords().length > 0) {
      <div class="block">
        <span class="label">{{ t('humanDesign.iching.practice.keywords') }}</span>
        <div class="chips">
          @for (word of keywords(); track word) {
            <button type="button" class="chip" data-test-id="iching-keyword" (click)="removeKeyword(word)">
              {{ word }} <i class="fas fa-xmark" aria-hidden="true"></i>
            </button>
          }
        </div>
      </div>
    }

    <label class="block">
      <span class="label">{{ t('humanDesign.iching.practice.notes') }}</span>
      <textarea
        #notesField
        class="notes"
        rows="4"
        data-test-id="iching-notes"
        [attr.maxlength]="notesMaxLength"
        [placeholder]="t('humanDesign.iching.practice.notesPlaceholder')"
        [value]="notes()"
        (input)="notesChange.emit(notesField.value)"
      ></textarea>
    </label>

    @if (revisit()) {
      <p class="revisit">{{ revisit() }}</p>
    }

    <div class="actions">
      <button type="button" class="primary" data-test-id="iching-save" [disabled]="saving()" (click)="save.emit()">
        {{ saving() ? t('humanDesign.iching.practice.saving') : t('humanDesign.iching.practice.save') }}
      </button>
      <button type="button" class="secondary" data-test-id="iching-copy" (click)="copy.emit()">
        <i class="fas fa-copy" aria-hidden="true"></i> {{ t('humanDesign.iching.practice.copy') }}
      </button>
      @if (canRevisit()) {
        <button type="button" class="secondary" data-test-id="iching-revisit" [disabled]="saving()" (click)="revisitRequested.emit()">
          <i class="fas fa-book-open-reader" aria-hidden="true"></i> {{ t('humanDesign.iching.practice.revisit') }}
        </button>
      }
      @if (saved() && !saving()) {
        <span class="saved" data-test-id="iching-saved">
          <i class="fas fa-check" aria-hidden="true"></i> {{ t('humanDesign.iching.practice.saved') }}
        </span>
      }
    </div>
  `,
})
export class HdIChingPracticeComponent {
  readonly keywords = input<readonly string[]>([]);
  readonly notes = input('');
  readonly doList = input<readonly string[]>([]);
  readonly dontList = input<readonly string[]>([]);
  readonly revisit = input<string | undefined>(undefined);
  readonly saving = input(false, { transform: booleanAttribute });
  readonly saved = input(false, { transform: booleanAttribute });
  /** Bewaarde raadpleging: knop "vandaag herlezen". */
  readonly canRevisit = input(false, { transform: booleanAttribute });

  readonly keywordsChange = output<readonly string[]>();
  readonly notesChange = output<string>();
  readonly doListChange = output<readonly string[]>();
  readonly dontListChange = output<readonly string[]>();
  readonly save = output<void>();
  readonly copy = output<void>();
  readonly revisitRequested = output<void>();

  protected readonly t = injectIChingTranslate();
  protected readonly kinds: readonly PracticeList[] = ['do', 'dont'];
  protected readonly notesMaxLength = ICHING_LIMITS.notes;

  protected listOf(kind: PracticeList): readonly string[] {
    return kind === 'do' ? this.doList() : this.dontList();
  }

  protected add(kind: PracticeList, field: HTMLInputElement): void {
    const value = field.value.trim();
    if (value.length === 0) {
      return;
    }
    field.value = '';
    if (this.listOf(kind).includes(value)) {
      return;
    }
    this.emitList(kind, [...this.listOf(kind), value]);
  }

  protected remove(kind: PracticeList, index: number): void {
    this.emitList(kind, this.listOf(kind).filter((_, i) => i !== index));
  }

  protected removeKeyword(word: string): void {
    this.keywordsChange.emit(this.keywords().filter(keyword => keyword !== word));
  }

  private emitList(kind: PracticeList, list: readonly string[]): void {
    if (kind === 'do') {
      this.doListChange.emit(list);
    } else {
      this.dontListChange.emit(list);
    }
  }
}
